import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useOrganizationUsers } from '@/hooks/use-organization-users';
import { formatFieldValue, getOption } from '@/lib/record-form/format';
import type { IModuleField } from '@/features/module-builder/types';
import { RatingInput } from './rating-input';
import { getUserDisplayName, getUserInitials } from './user-display';

export interface RecordFieldValueProps {
    field: IModuleField;
    value: unknown;
}

function isEmptyDisplayValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return true;
    return Array.isArray(value) && value.length === 0;
}

function truncateId(id: string): string {
    return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function OptionBadge({ field, value }: { field: IModuleField; value: unknown }) {
    const option = getOption(field, value);
    const label = option?.label ?? String(value);
    if (!option?.color) return <Badge>{label}</Badge>;
    return (
        <span
            className="inline-flex h-5 w-fit shrink-0 items-center rounded-md px-2 text-xs font-medium whitespace-nowrap"
            style={{ backgroundColor: `${option.color}20`, color: option.color }}
        >
            {label}
        </span>
    );
}

/** Separate component so the org-users query only mounts for USER fields. */
function UserValue({ userId }: { userId: string }) {
    const usersQuery = useOrganizationUsers();
    const user = (usersQuery.data?.data ?? []).find((candidate) => candidate.id === userId);

    if (!user) {
        return <span className="text-sm text-text-secondary">{truncateId(userId)}</span>;
    }
    return (
        <span className="inline-flex items-center gap-2">
            <Avatar size="sm">
                {user.avatarUrl && (
                    <AvatarImage src={user.avatarUrl} alt={getUserDisplayName(user)} />
                )}
                <AvatarFallback className="text-[10px]">{getUserInitials(user)}</AvatarFallback>
            </Avatar>
            <span className="text-sm text-text-primary">{getUserDisplayName(user)}</span>
        </span>
    );
}

/**
 * Read-only rich renderer for one field value (record detail panes, quick
 * views). Plain-text fallbacks come from `formatFieldValue`.
 */
export function RecordFieldValue({ field, value }: RecordFieldValueProps) {
    const { t } = useTranslation();

    if (isEmptyDisplayValue(value)) {
        return <span className="text-text-tertiary">—</span>;
    }

    switch (field.fieldType) {
        case 'BOOLEAN': {
            const isTruthy = value === true || value === 'true';
            return (
                <span className="inline-flex items-center gap-1.5 text-sm text-text-primary">
                    {isTruthy ? (
                        <Check className="size-4 shrink-0 text-success-600" aria-hidden="true" />
                    ) : (
                        <X className="size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
                    )}
                    {isTruthy ? t('common.yes', 'Yes') : t('common.no', 'No')}
                </span>
            );
        }

        case 'SELECT':
            return <OptionBadge field={field} value={value} />;

        case 'MULTI_SELECT': {
            const items = Array.isArray(value) ? value : [value];
            return (
                <span className="flex flex-wrap gap-1">
                    {items.map((item) => (
                        <OptionBadge key={String(item)} field={field} value={item} />
                    ))}
                </span>
            );
        }

        case 'RATING':
            return <RatingInput value={Number(value) || 0} settings={field.settings} readOnly />;

        case 'URL': {
            const href = String(value);
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block max-w-full truncate align-bottom text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    {href}
                </a>
            );
        }

        case 'EMAIL': {
            const email = String(value);
            return (
                <a
                    href={`mailto:${email}`}
                    className="inline-block max-w-full truncate align-bottom text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    {email}
                </a>
            );
        }

        case 'TEXTAREA':
            return (
                <span className="text-sm wrap-break-word whitespace-pre-wrap text-text-primary">
                    {String(value)}
                </span>
            );

        case 'USER':
            return (
                <UserValue userId={Array.isArray(value) ? String(value[0] ?? '') : String(value)} />
            );

        case 'LOOKUP':
            return (
                <span className="inline-block max-w-48 truncate align-bottom text-sm text-text-primary">
                    {formatFieldValue(field, value)}
                </span>
            );

        default:
            return (
                <span className="text-sm text-text-primary">{formatFieldValue(field, value)}</span>
            );
    }
}
